/**
 * ============================================================
 * KEJJA OS — src/hooks/useAdmin.ts (v4)
 * ============================================================
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '@/lib/adminApi';

export function useAdminSession() {
  return useQuery({
    queryKey: ['admin', 'me'],
    queryFn: () => adminApi.me().then(r => r.user).catch(() => null),
    retry: false, staleTime: 30_000, gcTime: 0,
  });
}

export function useAdminLogin() {
  return useMutation({ mutationFn: ({ email, password }: { email: string; password: string }) => adminApi.login(email, password) });
}

export function useAdminVerifyOtp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ otpSessionId, code, rememberMe }: { otpSessionId: string; code: string; rememberMe?: boolean }) =>
      adminApi.verifyOtp(otpSessionId, code, rememberMe),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'me'] }),
  });
}

export function useAdminLogout() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: () => adminApi.logout(), onSuccess: () => qc.clear() });
}

export function useAdminStats() { return useQuery({ queryKey: ['admin', 'stats'], queryFn: () => adminApi.stats() }); }
export function useAdminGrowth(days = 30) { return useQuery({ queryKey: ['admin', 'growth', days], queryFn: () => adminApi.growth(days) }); }
export function useAdminFinance() { return useQuery({ queryKey: ['admin', 'analytics', 'finance'], queryFn: () => adminApi.financeSnapshot() }); }
export function useAdminContent() { return useQuery({ queryKey: ['admin', 'analytics', 'content'], queryFn: () => adminApi.contentSnapshot() }); }
export function useAdminEngagement() { return useQuery({ queryKey: ['admin', 'analytics', 'engagement'], queryFn: () => adminApi.engagementSnapshot() }); }
export function useAdminSystem() { return useQuery({ queryKey: ['admin', 'system'], queryFn: () => adminApi.systemInfo(), refetchInterval: 15_000 }); }

export function useAdminUsers(opts: { limit?: number; offset?: number; search?: string }) {
  return useQuery({ queryKey: ['admin', 'users', opts], queryFn: () => adminApi.users(opts) });
}
export function useAdminUserDetail(userId: string | null) {
  return useQuery({
    queryKey: ['admin', 'users', 'detail', userId],
    queryFn: () => adminApi.userDetail(userId!), enabled: !!userId,
  });
}
export function useAdminHomes(opts: { limit?: number; offset?: number; search?: string }) {
  return useQuery({ queryKey: ['admin', 'homes', opts], queryFn: () => adminApi.homes(opts) });
}
export function useAdminHomeDetail(homeId: string | null) {
  return useQuery({
    queryKey: ['admin', 'homes', 'detail', homeId],
    queryFn: () => adminApi.homeDetail(homeId!), enabled: !!homeId,
  });
}
export function useAdminList() {
  return useQuery({ queryKey: ['admin', 'admins'], queryFn: () => adminApi.listAdmins() });
}
export function useGrantAdmin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (email: string) => adminApi.grantAdmin(email),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'admins'] }),
  });
}

export function useSetSuperAdmin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, value }: { userId: string; value: boolean }) => adminApi.setSuperAdmin(userId, value),
    onSuccess: (_, v) => {
      qc.invalidateQueries({ queryKey: ['admin', 'users'] });
      qc.invalidateQueries({ queryKey: ['admin', 'users', 'detail', v.userId] });
      qc.invalidateQueries({ queryKey: ['admin', 'admins'] });
    },
  });
}
export function useDeactivateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => adminApi.deactivateUser(userId),
    onSuccess: (_, userId) => {
      qc.invalidateQueries({ queryKey: ['admin', 'users'] });
      qc.invalidateQueries({ queryKey: ['admin', 'users', 'detail', userId] });
    },
  });
}
export function useReactivateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => adminApi.reactivateUser(userId),
    onSuccess: (_, userId) => {
      qc.invalidateQueries({ queryKey: ['admin', 'users'] });
      qc.invalidateQueries({ queryKey: ['admin', 'users', 'detail', userId] });
    },
  });
}
export function useRevokeSessions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => adminApi.revokeSessions(userId),
    onSuccess: (_, userId) => qc.invalidateQueries({ queryKey: ['admin', 'users', 'detail', userId] }),
  });
}
